<template>
  <div
    v-if="data.notesLoaded"
    class="i-coaching-meeting-notes"
  >
    <img
      class="back-button"
      :src="iconArrow"
      @click="goToCoachingHome"
    />
    <iBaseHeader
      highlight-text="Meeting Notes"
      :version="2"
      font-size-highlighted-text="16px"
      bottom-margin="30px"
      text=""
      align="start"
    />
    <div class="main">
      <div
        v-if="data.meetingNotes.length"
        class="list"
      >
        <iMeetingNoteCard
          v-for="(meetingNote, index) in data.meetingNotes"
          :key="meetingNote.id"
          :meeting-notes="meetingNote"
          :same-stage-as-last="index > 0 ? data.meetingNotes[index - 1].careerStage == meetingNote.careerStage : false"
          :same-stage-as-next="
            index < data.meetingNotes.length - 1
              ? data.meetingNotes[index + 1].careerStage == meetingNote.careerStage
              : false
          "
          @refresh-meeting-notes="fetchMeetingNotes"
        />
      </div>
      <div
        v-else
        class="empty"
      >
        No meeting notes to display.
      </div>
    </div>
    <div
      class="pagination"
      :class="{ 'is-report': userStore.isReportUser }"
    >
      <div
        class="button"
        :class="{ inactive: previousIsInactive }"
        @click="previousIsInactive ? null : fetchPrevious()"
      >
        Previous
      </div>
      <div>Page {{ data.page }}</div>
      <div
        class="button"
        :class="{ inactive: data.nextIsInactive }"
        @click="data.nextIsInactive ? null : fetchNext()"
      >
        Next
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { useRouter } from "vue-router"
import { useUserStore } from "@/stores/user"

import scrollToTop from "@/composables/scrollToTop"
import { GlobalVariables } from "@/helpers/globalVariables"
import api from "@/services/apiClient"

import iBaseHeader from "@/components/base/BaseHeader.vue"
import iMeetingNoteCard from "@/components/coaching/home/MeetingNoteCard.vue"

// ASSETS
import iconArrow from "@/assets/icons/icon-arrow-circle-left.svg"

// TYPES
import { MeetingNotesUserAccess } from "@/shared/types/MeetingNotes"

// STORES AND ROUTERS
const userStore = useUserStore()
const router = useRouter()

// DATA
const data = reactive({
  notesLoaded: false,
  meetingNotes: [] as MeetingNotesUserAccess[],
  page: 1,
  nextIsInactive: true
})

// COMPUTED
const previousIsInactive = computed(() => data.page <= 1)

// FUNCTIONS
const fetchMeetingNotes = async () => {
  if (userStore.getUserId) {
    try {
      const response = await api.meeting.getListForClient(userStore.getUserId, {
        limit: 10,
        page: data.page,
        orderBy: "date",
        direction: "desc",
        status: ["assistedDelivered", "delivered"],
        type: [
          "discovery",
          "coaching",
          "executiveCoaching",
          "recurring",
          "onDemand",
          "scoping",
          "scopingRescheduled",
          "proPackage"
        ]
      })
      data.meetingNotes = response.meetingNotes

      if (data.meetingNotes.length < 10) data.nextIsInactive = true
      else {
        const nextBatch = await api.meeting.getListForClient(userStore.getUserId, {
          limit: 10,
          page: data.page + 1,
          orderBy: "date",
          direction: "desc",
          status: ["assistedDelivered", "delivered"],
          type: [
            "discovery",
            "coaching",
            "executiveCoaching",
            "recurring",
            "onDemand",
            "scoping",
            "scopingRescheduled",
            "proPackage"
          ]
        })
        data.nextIsInactive = nextBatch.meetingNotes.length == 0
      }
    } catch {
      console.error(`Error fetching user's meeting notes. User ID: ${userStore.getUserId}`)
    }
  }
}

const fetchPrevious = async () => {
  data.page -= 1
  await fetchMeetingNotes()
  scrollToTop()
}

const fetchNext = async () => {
  data.page += 1
  await fetchMeetingNotes()
  scrollToTop()
}

const goToCoachingHome = () => {
  router.push({
    name: GlobalVariables.urls.coaching.children.home.routeName
  })
}

// LIFECYCLE HOOKS
onMounted(async () => {
  await fetchMeetingNotes()
  data.notesLoaded = true
})
</script>

<style scoped lang="scss">
.i-coaching-meeting-notes {
  position: relative;

  .back-button {
    @include border-radius-rounded;

    position: absolute;
    left: -21px;
    top: -18px;
    cursor: pointer;

    @media screen and (min-width: $breakpoint-md) {
      left: -38px;
      top: 6px;
    }

    @media screen and (min-width: $breakpoint-xxl) {
      left: -58px;
      top: 6px;
    }

    &:hover {
      background-color: $black100;
    }
  }

  .main {
    .list {
      @include flex(column, start, start);
    }

    .empty {
      @include text-lg;

      color: $black400;
    }
  }

  .pagination {
    @include flex(row, center, center, $gap: 70px);

    position: fixed;
    bottom: 0;
    background-color: white;
    padding: 12px 0;
    left: 50%;
    transform: translate(-50%, 0);
    width: 100%;
    box-shadow: 0 4px 16px 0 $black100;

    &.is-report {
      @media screen and (max-width: $breakpoint-lg) {
        bottom: 82px;
      }
    }

    .button {
      font-weight: 600;
      cursor: pointer;

      &.inactive {
        color: $black300;
        cursor: not-allowed;
      }
    }
  }
}
</style>
